import { isProfileFormComplete } from "./providerProfileFields";

export const ONBOARDING_STATUS = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
};

export const ONBOARDING_VISITED_KEY = "numente_onboarding_visited";
/** @deprecated Approval flow backlog — kept for localStorage cleanup only */
export const ONBOARDING_SUBMITTED_KEY = "numente_onboarding_submitted";

export const ONBOARDING_STEPS = [
  {
    id: "profile",
    step: 1,
    title: "Profile Information",
    description: "Your basic details, display name, photo, and bio highlights.",
    path: "/onboarding/profile-information",
  },
  {
    id: "credentials",
    step: 2,
    title: "Licenses & Credentials",
    description: "Review and confirm your state licenses.",
    path: "/onboarding/licenses-credentials",
  },
  {
    id: "payments",
    step: 3,
    title: "Payments Accepted",
    description: "Review and set your self-pay per session fee.",
    path: "/onboarding/payments-accepted",
  },
  {
    id: "availability",
    step: 4,
    title: "Availability",
    description: "Set your availability.",
    path: "/onboarding/availability",
  },
  {
    id: "settings",
    step: 5,
    title: "Settings",
    description: "Configure referrals, notifications, and account preferences.",
    path: "/onboarding/settings",
  },
  {
    id: "continue",
    step: 6,
    title: "Review and Continue",
    description:
      "Review your setup and continue to your full provider dashboard.",
    path: null,
  },
];

/** Provider statuses that still show the onboarding dashboard */
export const ONBOARDING_PROVIDER_STATUSES = ["Incomplete", "Invited"];

export const isProviderPendingReview = (userOrProvider) => {
  const status = String(
    userOrProvider?.providerStatus || userOrProvider?.status || "",
  ).trim();
  return status === "Pending" || status === "Pending Review";
};

export const markOnboardingSubmitted = (providerId) => {
  if (!providerId) return;
  try {
    localStorage.setItem(
      ONBOARDING_SUBMITTED_KEY,
      JSON.stringify({ providerId: String(providerId), at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
};

export const clearOnboardingSubmitted = () => {
  try {
    localStorage.removeItem(ONBOARDING_SUBMITTED_KEY);
  } catch {
    /* ignore */
  }
};

export const hasOnboardingSubmitted = (providerId) => {
  if (!providerId) return false;
  try {
    const raw = localStorage.getItem(ONBOARDING_SUBMITTED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return String(parsed?.providerId || "") === String(providerId);
  } catch {
    return false;
  }
};

const resolveStatus = (isComplete, isStarted) => {
  if (isComplete) return ONBOARDING_STATUS.COMPLETED;
  if (isStarted) return ONBOARDING_STATUS.IN_PROGRESS;
  return ONBOARDING_STATUS.NOT_STARTED;
};

/**
 * Prefill from Super Admin must NOT auto-complete a step.
 * Status stays Not Started until the provider enters the section (visited).
 * Then: Completed if requirements are met, otherwise In Progress.
 */
const resolveVisitedStepStatus = (visited, sectionId, requirementsMet) => {
  if (!visited?.[sectionId]) return ONBOARDING_STATUS.NOT_STARTED;
  return resolveStatus(requirementsMet, true);
};

export const getVisitedSections = (providerId) => {
  try {
    const raw = localStorage.getItem(ONBOARDING_VISITED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Legacy shape: { profile: true, ... }
    if (!parsed?.byProvider) {
      if (providerId && parsed && typeof parsed === "object") {
        return parsed;
      }
      return parsed && typeof parsed === "object" ? parsed : {};
    }
    if (!providerId) return {};
    return parsed.byProvider[String(providerId)] || {};
  } catch {
    return {};
  }
};

export const markSectionVisited = (sectionId, providerId) => {
  if (!sectionId || sectionId === "continue") return;
  try {
    const raw = localStorage.getItem(ONBOARDING_VISITED_KEY);
    let store = {};
    if (raw) {
      try {
        store = JSON.parse(raw) || {};
      } catch {
        store = {};
      }
    }

    // Migrate legacy flat map into per-provider shape when we have an id.
    if (!store.byProvider) {
      const legacy =
        store && typeof store === "object" && !Array.isArray(store) ? store : {};
      store = { byProvider: {} };
      if (providerId && Object.keys(legacy).length > 0) {
        store.byProvider[String(providerId)] = legacy;
      }
    }

    const key = providerId ? String(providerId) : "_default";
    const current = store.byProvider[key] || {};
    if (current[sectionId]) return;

    store.byProvider[key] = { ...current, [sectionId]: true };
    localStorage.setItem(ONBOARDING_VISITED_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
};

export const clearOnboardingVisited = () => {
  localStorage.removeItem(ONBOARDING_VISITED_KEY);
};

const hasText = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const parseCredentials = (credentials) => {
  if (Array.isArray(credentials)) return credentials;
  if (typeof credentials === "string") {
    try {
      const parsed = JSON.parse(credentials);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const getProfileStepStatus = (provider, visited = {}) => {
  const requirementsMet = isProfileFormComplete(provider);
  return resolveVisitedStepStatus(visited, "profile", requirementsMet);
};

export const hasValidCredentials = (provider) => {
  const credentials = parseCredentials(provider?.credentials);
  return credentials.some((item) => {
    const status = String(item?.status || "Active").toLowerCase();
    if (status === "inactive") return false;
    return hasText(item?.state) && hasText(item?.licenseNumber);
  });
};

export const getCredentialsStepStatus = (provider, visited = {}) => {
  const requirementsMet = hasValidCredentials(provider);

  // Prefill must not auto-complete. Opening the section → In Progress.
  // Completed after the provider confirms the review (no edits required).
  if (!visited?.credentials) return ONBOARDING_STATUS.NOT_STARTED;
  if (!visited?.credentialsConfirmed) return ONBOARDING_STATUS.IN_PROGRESS;
  return resolveStatus(requirementsMet, true);
};

/** Mark credentials as reviewed so the checklist can move to Completed. */
export const markCredentialsConfirmed = (providerId) => {
  markSectionVisited("credentials", providerId);
  markSectionVisited("credentialsConfirmed", providerId);
};

export const isCredentialsConfirmed = (providerId) =>
  Boolean(getVisitedSections(providerId)?.credentialsConfirmed);

export const getPaymentsStepStatus = (provider, visited = {}) => {
  const hasFee =
    provider?.fees !== null &&
    provider?.fees !== undefined &&
    String(provider.fees).trim() !== "";
  return resolveVisitedStepStatus(visited, "payments", hasFee);
};

export const getAvailabilityStepStatus = (hasSlots, visited = {}) =>
  resolveVisitedStepStatus(visited, "availability", Boolean(hasSlots));

export const getSettingsStepStatus = (provider, visited = {}) => {
  // Settings is complete once the provider opens/reviews the section.
  return resolveVisitedStepStatus(visited, "settings", Boolean(visited.settings));
};

export const getActionLabel = (status) => {
  if (status === ONBOARDING_STATUS.COMPLETED) return "Edit";
  if (status === ONBOARDING_STATUS.IN_PROGRESS) return "Continue";
  return "Start";
};

export const buildOnboardingChecklist = ({
  provider,
  hasAvailabilitySlots = false,
  visited = getVisitedSections(),
}) => {
  const statuses = {
    profile: getProfileStepStatus(provider, visited),
    credentials: getCredentialsStepStatus(provider, visited),
    payments: getPaymentsStepStatus(provider, visited),
    availability: getAvailabilityStepStatus(hasAvailabilitySlots, visited),
    settings: getSettingsStepStatus(provider, visited),
  };

  const requiredComplete = [
    statuses.profile,
    statuses.credentials,
    statuses.payments,
    statuses.availability,
    statuses.settings,
  ].every((status) => status === ONBOARDING_STATUS.COMPLETED);

  return {
    steps: ONBOARDING_STEPS.map((step) => {
      if (step.id === "continue") {
        return {
          ...step,
          status: requiredComplete
            ? ONBOARDING_STATUS.COMPLETED
            : ONBOARDING_STATUS.NOT_STARTED,
          actionLabel: "Continue",
          canContinue: requiredComplete,
        };
      }

      const status = statuses[step.id];
      return {
        ...step,
        status,
        actionLabel: getActionLabel(status),
        canContinue: false,
      };
    }),
    requiredComplete,
    statuses,
  };
};

export const needsProviderOnboarding = (user) => {
  if (!user) return false;
  const isBypassRole =
    user.role === "careManager" ||
    user.role === "psychiatricConsultant" ||
    user.providerType === "Care Manager" ||
    user.providerType === "Psychiatric Consultant";

  return (
    user.role === "provider" &&
    !isBypassRole &&
    ONBOARDING_PROVIDER_STATUSES.includes(user.providerStatus)
  );
};

/** @deprecated Use needsProviderOnboarding — kept for existing imports */
export const isIncompleteTherapist = needsProviderOnboarding;

export const INCOMPLETE_THERAPIST_ALLOWED_PATHS = [
  "/dashboard",
  "/onboarding",
  "/change-password",
  "/update-password",
];

export const isIncompleteAllowedPath = (pathname) =>
  INCOMPLETE_THERAPIST_ALLOWED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

/** True while therapist must finish onboarding before using the rest of the app. */
export const isOnboardingNavigationLocked = (user) =>
  needsProviderOnboarding(user);